import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useClipboard, useDisclosure } from '@chakra-ui/react'
import { generateRandomName } from '@/lib/utils/random-name'
import {
  validateAccountName,
  signUpAccount,
  signInWithApiKey,
  maskSecretKey,
  commitApiKey,
  clearApiKeyAndNotify,
  getStoredApiKey,
} from '@/lib/account/auth'
import {
  validateApiKey,
  revokeApiKey,
  updateApiKeyName,
  listApiKeyAgents,
  ApiError,
  type ApiKeyResponse,
  type ApiKeyStats,
  type AgentConnection,
} from '@/lib/api/keys'

type View = 'input' | 'showKey' | 'dashboard'

/**
 * Manages account authentication, signup, signin, key validation,
 * name editing, account destruction, and agent loading for SettingsPage.
 */
export function useSettingsAccount() {
  const navigate = useNavigate()
  const location = useLocation()

  // Start with null view to prevent flash of login UI while checking stored key
  const [view, setView] = useState<View | null>(() => {
    const storedKey = getStoredApiKey()
    return storedKey ? null : 'input'
  })
  const [apiKey, setApiKey] = useState('')
  const [inputKey, setInputKey] = useState('')
  const [stats, setStats] = useState<ApiKeyStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newKeyData, setNewKeyData] = useState<ApiKeyResponse | null>(null)
  const [signUpName, setSignUpName] = useState('')
  const [signUpError, setSignUpError] = useState<string | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [termsError, setTermsError] = useState(false)
  const { hasCopied, onCopy } = useClipboard(newKeyData?.apiKey || '')

  const isLoginPage = false // Login now handled by editor modal
  const redirectTo = (location.state as { from?: string })?.from || '/editor'

  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [savingName, setSavingName] = useState(false)

  // Modal for revoking API key
  const { isOpen: isRevokeOpen, onOpen: onRevokeOpen, onClose: onRevokeClose } = useDisclosure()

  // Secret key display state
  const [showSecretKey, setShowSecretKey] = useState(false)
  const { hasCopied: hasSecretKeyCopied, onCopy: onSecretKeyCopy } = useClipboard(apiKey)

  // Connected agents state
  const [agents, setAgents] = useState<AgentConnection[]>([])
  const [loadingAgents, setLoadingAgents] = useState(false)

  // --- Auto-validate stored key on mount ---

  useEffect(() => {
    const storedKey = getStoredApiKey()
    if (storedKey) {
      setInputKey(storedKey)
      validateStoredKey(storedKey)
    } else if (!isLoginPage) {
      navigate('/editor?auth=signup', { replace: true })
    }
  }, [isLoginPage, navigate])

  // Listen for sign out events and redirect
  useEffect(() => {
    const handleApiKeyChange = () => {
      const storedKey = getStoredApiKey()
      const onSettingsPage = window.location.pathname === '/settings'
      if (!storedKey && onSettingsPage) {
        window.location.href = '/editor?auth=signup'
      }
    }

    window.addEventListener('apikey-changed', handleApiKeyChange)
    return () => window.removeEventListener('apikey-changed', handleApiKeyChange)
  }, [])

  // --- Agent loading ---

  const loadAgents = async (key: string) => {
    setLoadingAgents(true)
    try {
      const response = await listApiKeyAgents(key)
      setAgents(response.agents)
    } catch {
      // Silently fail - agents list is not critical
    } finally {
      setLoadingAgents(false)
    }
  }

  // --- Key validation ---

  const validateStoredKey = async (key: string, shouldRedirect = false) => {
    setLoading(true)
    setError(null)
    try {
      const statsData = await validateApiKey(key)
      if (!statsData.valid) {
        clearApiKeyAndNotify()
        navigate('/editor?auth=signup', { replace: true })
        return
      }

      setApiKey(key)
      setStats(statsData)
      loadAgents(key)

      if (isLoginPage) {
        if (shouldRedirect) {
          navigate(redirectTo, { replace: true })
        } else {
          navigate('/settings', { replace: true })
        }
      } else {
        setView('dashboard')
      }
    } catch {
      clearApiKeyAndNotify()
      navigate('/editor?auth=signup', { replace: true })
    } finally {
      setLoading(false)
    }
  }

  // --- Sign up ---

  const handleSignUp = useCallback(async () => {
    const nameCheck = validateAccountName(signUpName)
    if (!nameCheck.valid) {
      setSignUpError(nameCheck.error)
      return
    }

    if (!acceptedTerms) {
      setTermsError(true)
      return
    }

    setSignUpError(null)
    setTermsError(false)
    setLoading(true)
    setError(null)
    try {
      const data = await signUpAccount(signUpName)
      setNewKeyData(data)
      setView('showKey')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }, [signUpName, acceptedTerms])

  const handleSignUpComplete = useCallback(() => {
    if (newKeyData) {
      commitApiKey(newKeyData.apiKey)
      setInputKey(newKeyData.apiKey)
      navigate(redirectTo, { replace: true })
    }
  }, [newKeyData, navigate, redirectTo])

  const handleGetStarted = useCallback(async () => {
    if (!acceptedTerms) {
      setTermsError(true)
      return
    }

    setTermsError(false)
    setLoading(true)
    setError(null)
    try {
      const randomName = generateRandomName()
      const data = await signUpAccount(randomName)
      setNewKeyData(data)
      setSignUpName(randomName)
      setView('showKey')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }, [acceptedTerms])

  // --- Sign in (validate input key) ---

  const handleValidateKey = useCallback(async () => {
    if (!inputKey.trim()) {
      setError('Please enter an API key')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await signInWithApiKey(inputKey)
      if (!result.valid) {
        setError(result.error)
        return
      }

      commitApiKey(inputKey)
      setApiKey(inputKey)
      setStats(result.stats)

      if (isLoginPage) {
        navigate(redirectTo, { replace: true })
      } else {
        setView('dashboard')
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to validate key')
    } finally {
      setLoading(false)
    }
  }, [inputKey, isLoginPage, navigate, redirectTo])

  // --- Revoke / destroy account ---

  const handleRevokeApiKey = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await revokeApiKey(apiKey)
      clearApiKeyAndNotify()
      onRevokeClose()
      navigate('/editor?auth=signup', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to revoke API key')
    } finally {
      setLoading(false)
    }
  }, [apiKey, onRevokeClose, navigate])

  // --- Name editing ---

  const handleStartEditName = () => {
    setEditedName(stats?.name || '')
    setIsEditingName(true)
  }

  const handleSaveName = useCallback(async () => {
    setSavingName(true)
    setError(null)
    try {
      const result = await updateApiKeyName(apiKey, editedName.trim() || null)
      if (result.success) {
        setStats((prev) => prev ? { ...prev, name: result.name } : null)
        setIsEditingName(false)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update name')
    } finally {
      setSavingName(false)
    }
  }, [apiKey, editedName])

  const handleCancelEditName = () => {
    setIsEditingName(false)
    setEditedName('')
  }

  return {
    view,
    apiKey,
    inputKey,
    setInputKey,
    stats,
    loading,
    error,
    newKeyData,
    signUpName,
    setSignUpName,
    signUpError,
    setSignUpError,
    acceptedTerms,
    setAcceptedTerms,
    termsError,
    setTermsError,
    hasCopied,
    onCopy,
    // Name editing
    isEditingName,
    editedName,
    setEditedName,
    savingName,
    handleStartEditName,
    handleSaveName,
    handleCancelEditName,
    // Revoke modal
    isRevokeOpen,
    onRevokeOpen,
    onRevokeClose,
    // Secret key display
    showSecretKey,
    setShowSecretKey,
    hasSecretKeyCopied,
    onSecretKeyCopy,
    maskSecretKey,
    // Agents
    agents,
    loadingAgents,
    // Handlers
    handleSignUp,
    handleSignUpComplete,
    handleGetStarted,
    handleValidateKey,
    handleRevokeApiKey,
  }
}
